import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

import { reportError } from '@/utils/error'
import store from '@/store'
import { getToken } from '@/utils/auth'
import analytics from '@/utils/analytics'
import DefaultLayout from '@/layouts/default'
import AuthLayout from '@/layouts/auth'

// 全局处理 重定向导致的 导航故障
const { isNavigationFailure, NavigationFailureType } = VueRouter
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject)
    return originalPush.call(this, location, onResolve, onReject)

  return originalPush.call(this, location).catch(err => {
    if (!isNavigationFailure(err, NavigationFailureType.redirected)) {
      reportError(err, '导航故障', location)
    }

    // resolve err
    return err
  })
}

Vue.use(VueRouter)
Vue.use(VueMeta, {
  refreshOnceOnNavigation: true
})

const checkFirstLoad = function(from, pathname) {
  const { path, matched = [] } = from || {}
  const isCurrentPath = `/${location.pathname}`.includes(pathname)
  return path === '/' && !matched.length && isCurrentPath
}

const routes = [
  {
    path: '/',
    name: 'Home',
    redirect: '/course'
  },
  {
    path: '/course',
    component: DefaultLayout,
    meta: {
      auth: true,
      perm: true
    },
    children: [
      {
        path: '',
        component: () => import('@/views/course/index'),
        name: 'course-list',
        meta: {
          title: '我的课程'
        }
      },
      {
        path: ':courseId',
        component: () => import('@/views/course/detail/index'),
        name: 'course-detail',
        redirect: ':courseId/info',
        children: [
          {
            path: 'info',
            component: () => import('@/views/course/detail/info')
          },
          {
            path: 'courseware',
            component: () => import('@/views/course/detail/courseware')
          },
          {
            path: 'works',
            component: () => import('@/views/course/works/index')
          },
          {
            path: 'works/new',
            component: () => import('@/views/course/detail/work-detail-edit')
          },
          {
            path: 'work/:workId',
            name: 'work-detail',
            component: () => import('@/views/course/detail/work-detail')
          },
          {
            path: 'work/:workId/edit',
            component: () => import('@/views/course/detail/work-detail-edit')
          },
          {
            path: 'students',
            component: () => import('@/views/course/detail/students')
          },
          {
            path: 'assistant',
            component: () => import('@/views/course/detail/assistant')
          },
          {
            path: 'signin',
            component: () => import('@/views/course/detail/sign-in')
          },
          {
            path: 'signin/:id',
            component: () => import('@/views/course/detail/sign-in-detail')
          }
        ]
      }
    ]
  },
  {
    path: '/grade',
    component: DefaultLayout,
    children: [
      {
        path: ':courseId/:workId',
        component: () => import('@/views/grade')
      }
    ]
  },
  {
    path: '/join',
    component: DefaultLayout,
    children: [
      {
        path: '/join/assistant/:code',
        component: () => import('@/views/join/assistant')
      },
      {
        path: '/join/:code',
        component: () => import('@/views/join')
      }
    ]
  },
  {
    path: '/no-course',
    meta: {
      auth: true
    },
    component: () => import('@/views/student-no-course')
  },
  {
    path: '/case',
    component: DefaultLayout,
    meta: {
      auth: true,
      perm: true,
      refreshPerm: true,
      feature: 'CaseLib'
    },
    children: [
      {
        path: '',
        component: () => import('@/views/case/index'),
        name: 'case-list',
        meta: {
          title: '精品案例库'
        }
      },
      {
        path: ':caseId',
        component: () => import('@/views/case/detail'),
        redirect: ':caseId/info',
        children: [
          {
            path: 'info',
            component: () => import('@/views/case/detail/info')
          },
          {
            path: 'usage',
            component: () => import('@/views/case/detail/usage')
          }
        ]
      }
    ]
  },
  {
    path: '/register',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'register',
        component: () => import('@/views/auth/register'),
        props: { type: 1 },
        meta: {
          nonLoggedRequired: true,
          title: '注册'
        }
      }
    ]
  },

  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/views/auth/login'),
        meta: {
          nonLoggedRequired: true,
          title: '登录'
        },
        beforeEnter: (to, from, next) => {
          if (
            !process.env.VUE_APP_PUBLIC_PATH ||
            process.env.VUE_APP_PUBLIC_PATH === '/' ||
            checkFirstLoad(from, '/login')
          ) {
            next()
          } else {
            location.href = to.fullPath
          }
        }
      }
    ]
  },

  {
    path: '/find-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        component: () => import('@/views/auth/register'),
        props: { type: 2 },
        meta: {
          nonLoggedRequired: true,
          title: '找回密码'
        }
      }
    ]
  },

  {
    path: '/appendix',
    component: DefaultLayout,
    children: [
      {
        path: 'copyright',
        component: () => import('@/views/appendix/copyright'),
        name: 'copyright',
        meta: {
          title: '版权声明'
        }
      },
      {
        path: 'manual',
        component: () => import('@/views/appendix/manual'),
        name: 'manual',
        meta: {
          title: '使用手册'
        }
      }
    ]
  },

  {
    path: '/404',
    component: DefaultLayout,
    children: [
      {
        path: '',
        component: () => import('@/views/404'),
        meta: {
          title: '页面不存在'
        }
      }
    ]
  },
  {
    path: '*',
    redirect: '/404'
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach(async (to, from, next) => {
  // query change don't trigger NProgress
  if (to.path !== from.path) {
    NProgress.start()
  }

  // 如果不包含这个功能 跳转到首页
  const isFeatureSupport = to.matched.every(record => {
    return !record.meta.feature || FEATURE[record.meta.feature]
  })

  if (!isFeatureSupport) {
    next({ path: '/' })
    NProgress.done()
    return
  }

  const needRefreshPerm = to.matched.some(record => record.meta.refreshPerm)

  // 权限的校验
  const hasToken = !!getToken()
  const hasUserInfo = store.getters['auth/hasUserInfo']
  if (hasToken && (!hasUserInfo || needRefreshPerm)) {
    await store.dispatch('auth/getInfo')
  }
  const hasPerm = store.state.auth.groups?.course

  const isNonLoggedRequired = to.matched.some(
    record => record.meta.nonLoggedRequired
  )
  const isAuthRequired = to.matched.some(record => record.meta.auth)
  const isPermRequired = to.matched.some(record => record.meta.perm)

  // route interceptor
  if (hasToken && isNonLoggedRequired) {
    next({ path: '/' })
  } else if (!hasToken && isAuthRequired) {
    next(`/login?redirect=${to.path}`)
  } else if (!hasPerm && isPermRequired) {
    next('/no-course')
  } else {
    next()
  }
  NProgress.done()
})

router.afterEach(to => {
  analytics.setCustomUrl(to.fullPath)
  analytics.trackPageView(to.meta.title || document.title)

  NProgress.done()
})

router.onError(error => {
  if (/loading chunk \S+ failed/i.test(error.message)) {
    location.href = router.history?.pending?.fullPath
  }
})

export default router
